import { useEffect, useState } from 'react';
import { LuPencil } from 'react-icons/lu';
import { MdLockReset } from "react-icons/md";
import { RiDeleteBinLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/button';
import CustomModal from '../../components/CustomModal';
import MultiLineText from '../../components/MultiLineText';
import NewTableComponent from '../../components/NewTableComponent';
import UserAvatar from '../../components/UserAvatar';
import { deleteUser, fetchUsersData, resetDefaultPassword } from '../../store/Slices/UserSlice';
import '../../styles/Users.css';
import { dateMonthYear } from '../../utils/helper';
import { errorNotify, successNotify } from '../../utils/toast';
import AddUser from './Users/AddUser';
import EditUser from './Users/EditUser';

const Users = () => {
    const dispatch = useDispatch();

    const { data = [], meta, loading, deleteLoading, resetPasswordLoading } = useSelector((state) => state.users);

    const [currentPage, setCurrentPage] = useState(meta?.currentPage || 1);
    const [limit, setLimit] = useState(meta?.itemsPerPage || 10);
    const [addUserPopUp, setAddUserPopUp] = useState(false);
    const [deleteUserPopup, setDeleteUserPopup] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [editUserPopUp, setEditUserPopUp] = useState(false);
    const [editUserId, setEditUserId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [resetPasswordPopup, setResetPasswordPopup] = useState(false);
    const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
    const [searchFilters, setSearchFilters] = useState(null);

    const commonColumns = {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: true,
    }

    const columns = [
        {
            ...commonColumns,
            name: "User ID",
            key: "id",
            selector: (row) => row?.id,
            width: "110px",
            tableColumn: true,
            filterName: "userIdFilter",
            type: "number"
        },
        {
            ...commonColumns,
            name: "User Name",
            key: "username",
            modifyData: (row) => row?.username,
            tableColumn: true,
            filterName: "userNameFilter",
            type: "text",
            width: "280px",
            selector: (row) => {
                return <div className='d-flex align-items-center'>
                    <UserAvatar username={row?.username} image={row?.image} marginRight='7px' />
                    {
                        row?.username?.length > 20
                            ? <div className="two-line-ellipsis">
                                <MultiLineText text={row?.username} />
                            </div> : row?.username
                    }
                </div>
            },
        },
        {
            ...commonColumns,
            name: "Email",
            key: "email",
            selector: (row) => row?.email,
            modifyData: (row) => row?.email,
            tableColumn: true,
            filterName: "emailFilter",
            type: "text",
            width: "280px",
        },
        // {
        //     ...commonColumns,
        //     name: "Role",
        //     key: "role",
        //     selector: (row) => row?.my_role?.name,
        //     modifyData: (row) => row?.my_role?.name,
        //     tableColumn: true
        // },
        {
            ...commonColumns,
            name: "Permission Group",
            key: "permission_group",
            selector: (row) => (
                <div className='group_permissions'>
                    {
                        row?.my_role?.slug === "super-admin" ? "Super Admin" :
                            row?.group_permissions?.length === 0 ? "No Group" : row?.group_permissions?.map((group) => group?.name).join(", ")
                    }
                </div>
            ),
            modifyData: (row) => row?.group_permissions?.length === 0 ? "No Group" : row?.group_permissions?.map((group) => group?.name).join(", "),
            tableColumn: false
        },
        {
            ...commonColumns,
            name: "Date of Joining",
            key: "doj",
            selector: (row) => row?.created_at ? dateMonthYear(row?.created_at) : "",
            modifyData: (row) => row?.created_at ? dateMonthYear(row?.created_at) : "",
            tableColumn: true,
            filterName: "joiningDateFilter",
            type: "date"
        },
        {
            name: "Actions",
            key: "actions",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: false,
            selector: (row) => {
                return (
                    <div className="table_action_btn">
                        <button onClick={() => {
                            setResetPasswordPopup(true);
                            setResetPasswordUserId(row?.id);
                        }}>
                            <MdLockReset style={{ color: "blue", fontSize: "22px" }} />
                        </button>
                        <button onClick={() => handleEditUser(row)} className="edit_btn">
                            <LuPencil />
                        </button>
                        <button disabled={row?.my_role?.slug === "super-admin"} onClick={() => {
                            setDeleteUserPopup(true);
                            setDeleteUserId(row?.id);
                        }} className={`delete_btn ${row?.my_role?.slug === "super-admin" ? "disabled_delete_btn" : ""}`}>
                            <RiDeleteBinLine />
                        </button>
                    </div>
                )
            },
            sortable: false,
            width: "150px",
            tableColumn: false
        }
    ]

    const handleEditUser = (user) => {
        setEditUserId(user.id);
        setEditUserPopUp(true);
    };

    const handleDeleteUser = async () => {
        try {
            await dispatch(deleteUser(deleteUserId)).unwrap();
            successNotify('User deleted successfully');
            setDeleteUserPopup(false);
            setDeleteUserId(null);

            dispatch(fetchUsersData({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleResetPassword = async () => {
        try {
            await dispatch(resetDefaultPassword(resetPasswordUserId)).unwrap();
            successNotify('Password reset successfully');
            setResetPasswordPopup(false);
            setResetPasswordUserId(null);
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleSearch = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (searchText.length > 0 || searchFilters) {
            dispatch(fetchUsersData({
                page: currentPage,
                limit: limit,
                search_text: searchText,
                searchFilters: searchFilters
            }));
        }
        else {
            dispatch(fetchUsersData({
                page: currentPage,
                limit: limit,
            }));
        }
    }, [searchText, currentPage, limit, searchFilters]);

    const handleLimitChange = (newLimit) => {
        setCurrentPage(1);
        setLimit(newLimit);
    };

    return (
        <div>
            <AddUser show={addUserPopUp} onHide={() => setAddUserPopUp(false)} currentPage={currentPage} limit={limit} />
            <EditUser
                show={editUserPopUp}
                onHide={() => {
                    setEditUserPopUp(false);
                    setEditUserId(null);
                }}
                userId={editUserId}
                currentPage={currentPage}
                limit={limit}
                searchFilters={searchFilters}
            />
            <CustomModal show={deleteUserPopup} handleClose={() => setDeleteUserPopup(false)} size="md" heading="Delete User" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this User?</p>

                    <Button
                        cancelBtn={() => setDeleteUserPopup(false)}
                        submitBtn={handleDeleteUser}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                    />
                </div>
            </CustomModal>

            <CustomModal show={resetPasswordPopup} handleClose={() => setResetPasswordPopup(false)} size="md" heading="Reset Default Password" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to reset this user's password to default?</p>

                    <Button
                        cancelBtn={() => setResetPasswordPopup(false)}
                        submitBtn={handleResetPassword}
                        loading={resetPasswordLoading}
                        disabled={resetPasswordLoading}
                        submitText="Reset Password"
                        type="submit"
                    />
                </div>
            </CustomModal>

            <NewTableComponent
                btnName="Add User"
                showAddBtn={true}
                onAddClick={() => setAddUserPopUp(true)}
                columns={columns}
                data={data}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={handleLimitChange}
                totalItems={meta?.totalItems || 0}
                totalPages={meta?.totalPages || 1}
                onSearch={handleSearch}
                loading={loading}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
            />
        </div>
    )
}

export default Users